import { useState } from 'react'
import clsx from 'clsx'
import { track, useEditor } from 'tldraw'
import { useParams } from 'react-router-dom'
import { MonitorPlay } from 'lucide-react'
import { toast } from 'sonner'
import { TOOLS, LOCKED_TOOLS } from '../../components/ToolbarButton/ToolItems'
import ToolbarButton from '@/components/ToolbarButton'
import { useAppStore } from '@/store'
import { getCurrentDirection } from '@/i18n/config'
import { useTranslation } from '@/i18n'
import { apiService } from '@/service/api'
import { useExplainAsVideoEnabled } from '@/features/videoBrief/explainAsVideo'

interface BoarToolsProps {
  isLocked: boolean
  isZoomMode?: boolean
  isZoomOutMode?: boolean
  onCommentClick?: () => void
  onZoomClick?: () => void
  onZoomOutClick?: () => void
  onToolChange?: () => void
}

const BoardTools = track(({ isLocked, isZoomMode, isZoomOutMode, onCommentClick, onZoomClick, onZoomOutClick, onToolChange }: BoarToolsProps) => {
  const editor = useEditor()
  const { setBoardLock } = useAppStore()
  const { t } = useTranslation()
  const isRTL = getCurrentDirection() === 'rtl'
  // "Explain as video" for every file on this board (PRD: Explain as video).
  // V1 has no Tag Mapping control, so this is the one board action in the rail.
  const { board_id: boardId } = useParams()
  const explainVideoEnabled = useExplainAsVideoEnabled()
  const [requestingVideo, setRequestingVideo] = useState(false)

  const handleExplainAsVideo = async () => {
    if (!boardId || requestingVideo) return
    setRequestingVideo(true)
    try {
      const receipt = await apiService.createVideoBrief({ workspaceId: boardId })
      toast.success(t('data_catalog:video_brief.requested', { name: receipt.name }))
    } catch {
      toast.error(t('data_catalog:video_brief.error'))
    } finally {
      setRequestingVideo(false)
    }
  }

  // Keyboard shortcuts are now handled centrally in BoardCanvas.tsx
  // to avoid duplicate event listeners and conflicting behavior

  const handleToolClick = (toolName: string) => {
    if (!editor) return

    // Special handling for comment tool - open comment panel instead of setting tldraw tool
    if (toolName === 'comment') {
      onCommentClick?.()
      return
    }

    // Special handling for zoom tools - overlay-based, not tldraw tools
    if (toolName === 'zoom') {
      onZoomClick?.()
      return
    }
    if (toolName === 'zoom-out') {
      onZoomOutClick?.()
      return
    }

    // Notify parent that a non-comment tool was clicked (exits comment mode)
    onToolChange?.()
    editor.setCurrentTool(toolName)
  }

  const handleUnlockClick = () => {
    if (!editor) return
    editor.updateInstanceState({ isReadonly: false })
    editor.setCurrentTool('select')
    setBoardLock(false)
  }

  const toolsToMap = isLocked ? LOCKED_TOOLS : TOOLS

  return (
    <div className={clsx(
      // UI-22: 12px, matching the zoom control. The three floating canvas controls
      // (this rail, the pagination bar, the zoom cluster) sit on the same surface
      // and used two different radii; Putra: "no corner radius all the same".
      "flex flex-col absolute top-[50%] mt-[-252px] bg-white dark:bg-dark-card shadow-lg gap-4 px-3 py-4 rounded-xl border border-gray-200 dark:border-dark-border",
      isRTL ? 'right-4' : 'left-4'
    )}>
      {toolsToMap.map(({ nameKey, icon, type, disabled }) => (
        <ToolbarButton
          name={t(nameKey)}
          key={nameKey}
          isactive={type === 'zoom' ? !!isZoomMode : type === 'zoom-out' ? !!isZoomOutMode : (!(isZoomMode || isZoomOutMode) && editor.getCurrentToolId() === type)}
          onClick={() =>
            isLocked && type !== 'hand'
              ? handleUnlockClick()
              : handleToolClick(type)
          }
          disabled={disabled}
        >
          {icon}
        </ToolbarButton>
      ))}
      {explainVideoEnabled && boardId && (
        <ToolbarButton
          name={t('board:actions.explain_as_video', { defaultValue: 'Explain as video' })}
          onClick={() => void handleExplainAsVideo()}
          isactive={false}
          disabled={requestingVideo}
        >
          <MonitorPlay />
        </ToolbarButton>
      )}
    </div>
  )
})

export default BoardTools
